"use client";

import * as React from 'react';

/**
 * Where focus goes when a dialog or sheet closes: back to the control that
 * opened it. Radix returns focus to a DialogTrigger, but a dialog opened from
 * an ordinary button (controlled `open`) would otherwise leave focus on the
 * page body. The opener is read as the dialog opens, just before Radix moves
 * focus inside. A caller's own handlers run first and win if they prevent the
 * default.
 */
export function useReturnFocus(
  onOpenAutoFocus?: (event: Event) => void,
  onCloseAutoFocus?: (event: Event) => void
) {
  const opener = React.useRef<HTMLElement | null>(null);

  const handleOpen = React.useCallback(
    (event: Event) => {
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
      onOpenAutoFocus?.(event);
    },
    [onOpenAutoFocus]
  );

  const handleClose = React.useCallback(
    (event: Event) => {
      onCloseAutoFocus?.(event);
      if (event.defaultPrevented) return;
      const target = opener.current;
      opener.current = null;
      if (target && target.isConnected) {
        event.preventDefault();
        target.focus({ preventScroll: true });
      }
    },
    [onCloseAutoFocus]
  );

  return { onOpenAutoFocus: handleOpen, onCloseAutoFocus: handleClose };
}
